import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import placeApi from '../../services/placeApi';
import { localizedValue } from '../../localization/presentationLanguage';

const styles = {
  wrap: {
    position: 'relative',
    width: '100%',
  },
  input: {
    width: '100%',
    minWidth: 0,
    height: 50,
    borderRadius: 15,
    border: '1px solid rgba(218,165,32,0.18)',
    background: 'rgba(255,255,255,0.045)',
    color: '#FFF3C4',
    padding: '0 14px',
    fontSize: 15,
    outline: 'none',
    boxSizing: 'border-box',
    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.04)',
  },
  inputLoading: {
    border: '1px solid rgba(247,215,116,0.42)',
    boxShadow: '0 0 0 3px rgba(218,165,32,0.08), inset 0 1px 0 rgba(255,255,255,0.04)',
  },
  dropdown: {
    position: 'absolute',
    top: 'calc(100% + 8px)',
    left: 0,
    right: 0,
    zIndex: 40,
    borderRadius: 16,
    border: '1px solid rgba(218,165,32,0.18)',
    background: 'linear-gradient(180deg, rgba(19,10,34,0.98) 0%, rgba(10,7,20,0.98) 100%)',
    boxShadow: '0 22px 48px rgba(0,0,0,0.34)',
    overflow: 'hidden',
    maxHeight: 320,
    overflowY: 'auto',
  },
  item: {
    padding: '12px 14px',
    borderBottom: '1px solid rgba(255,255,255,0.05)',
    cursor: 'pointer',
    display: 'grid',
    gap: 4,
    background: 'transparent',
  },
  itemActive: {
    background: 'rgba(218,165,32,0.10)',
  },
  itemLabel: {
    color: '#FFF3C4',
    fontSize: 14,
    fontWeight: 800,
    lineHeight: 1.45,
  },
  itemMeta: {
    color: 'rgba(255,243,196,0.62)',
    fontSize: 12,
    lineHeight: 1.45,
  },
  empty: {
    padding: '12px 14px',
    color: 'rgba(255,243,196,0.70)',
    fontSize: 13,
    lineHeight: 1.5,
  },
  hint: {
    marginTop: 6,
    color: 'rgba(255,243,196,0.62)',
    fontSize: 12,
    lineHeight: 1.45,
  },
};

function formatMeta(place) {
  const parts = [place.region, place.country].filter(Boolean);
  return parts.join(' · ');
}

function normalizeSearchKey(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ');
}

export default function PlaceAutocomplete({
  value = '',
  onInputChange,
  onSelectPlace,
  placeholder,
  inputId,
  inputName,
  label,
  disabled = false,
  minChars = 2,
  limit = 8,
  hint,
}) {
  const resolvedPlaceholder = placeholder || localizedValue({ es: 'Elegí o escribí una ciudad', en: 'Choose or enter a city' });
  const resolvedLabel = label || localizedValue({ es: 'Ciudad de nacimiento', en: 'Birth city' });
  const resolvedHint = hint || localizedValue({ es: 'Escribí al menos 2 letras para ver sugerencias reales.', en: 'Enter at least 2 letters to see location suggestions.' });
  const [query, setQuery] = useState(String(value || ''));
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeIndex, setActiveIndex] = useState(-1);

  const rootRef = useRef(null);
  const requestIdRef = useRef(0);
  const lastCommittedSelectionRef = useRef('');
  const cacheRef = useRef(new Map());
  const lastFetchedQueryRef = useRef('');
  const autoInputId = useId();
  const resolvedInputId = inputId || `place-autocomplete-${autoInputId}`;
  const resolvedInputName = inputName || 'birthCity';

  function rememberCachedResult(cacheKey, value) {
    if (!cacheKey) return;

    if (cacheRef.current.has(cacheKey)) {
      cacheRef.current.delete(cacheKey);
    }

    cacheRef.current.set(cacheKey, Array.isArray(value) ? value : []);

    // Keep cache bounded so the autocomplete does not grow forever.
    if (cacheRef.current.size > 40) {
      const oldestKey = cacheRef.current.keys().next().value;
      if (oldestKey) cacheRef.current.delete(oldestKey);
    }
  }

  useEffect(() => {
    const nextValue = String(value || '');

    if (nextValue === query) return;

    setQuery(nextValue);

    if (!nextValue || nextValue === lastCommittedSelectionRef.current) {
      setResults([]);
      setOpen(false);
      setLoading(false);
      setError('');
      setActiveIndex(-1);
    }
  }, [value, query]);

  useEffect(() => {
    function handleOutsideClick(event) {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(event.target)) {
        setOpen(false);
        setActiveIndex(-1);
      }
    }

    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('touchstart', handleOutsideClick, { passive: true });

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('touchstart', handleOutsideClick);
    };
  }, []);

  useEffect(() => {
    const trimmed = String(query || '').trim();
    const normalizedQuery = normalizeSearchKey(trimmed);
    const currentRequestId = ++requestIdRef.current;

    if (trimmed && trimmed === lastCommittedSelectionRef.current) {
      setResults([]);
      setOpen(false);
      setLoading(false);
      setError('');
      setActiveIndex(-1);
      return undefined;
    }

    // Avoid repeating the exact same successful query (ignoring case/accents/extra spaces)
    if (normalizedQuery && normalizedQuery === lastFetchedQueryRef.current) {
      const cacheKey = `${normalizedQuery}::${limit}`;
      if (cacheRef.current.has(cacheKey)) {
        const cached = cacheRef.current.get(cacheKey);
        setResults(cached);
        setOpen(true);
        setActiveIndex(cached.length ? 0 : -1);
        setLoading(false);
        setError('');
        return undefined;
      }
    }

    if (trimmed.length < minChars || disabled) {
      setResults([]);
      setOpen(false);
      setLoading(false);
      setError('');
      setActiveIndex(-1);
      return undefined;
    }

    const cacheKey = `${normalizedQuery}::${limit}`;
    if (cacheRef.current.has(cacheKey)) {
      const cached = cacheRef.current.get(cacheKey);
      setResults(cached);
      setOpen(true);
      setActiveIndex(cached.length ? 0 : -1);
      setLoading(false);
      setError('');
      lastFetchedQueryRef.current = normalizedQuery;
      return undefined;
    }

    setLoading(true);
    setError('');

    const timer = setTimeout(async () => {
      try {
        const next = await placeApi.searchPlaces(trimmed, { limit });
        rememberCachedResult(cacheKey, next);
        lastFetchedQueryRef.current = normalizedQuery;
        if (requestIdRef.current !== currentRequestId) return;
        setResults(Array.isArray(next) ? next : []);
        setOpen(true);
        setActiveIndex((Array.isArray(next) && next.length) ? 0 : -1);
      } catch (err) {
        if (requestIdRef.current !== currentRequestId) return;
        setResults([]);
        setOpen(true);
        setActiveIndex(-1);
        setError(err?.message || 'No se pudieron cargar lugares ahora.');
      } finally {
        if (requestIdRef.current === currentRequestId) {
          setLoading(false);
        }
      }
    }, 380);

    return () => clearTimeout(timer);
  }, [query, disabled, limit, minChars]);

  const showDropdown = useMemo(() => {
    return open && !disabled && (loading || error || results.length > 0 || String(query || '').trim().length >= minChars);
  }, [open, disabled, loading, error, results, query, minChars]);

  function handleChange(event) {
    const next = event.target.value;
    if (next !== lastCommittedSelectionRef.current) {
      lastCommittedSelectionRef.current = '';
    }
    setQuery(next);
    setOpen(true);
    setActiveIndex(-1);
    if (typeof onInputChange === 'function') onInputChange(next);
  }

  function handleSelect(place) {
    const nextDisplay =
      place?.primaryName ||
      place?.district ||
      place?.city ||
      place?.label ||
      [place?.city, place?.country].filter(Boolean).join(', ');

    lastCommittedSelectionRef.current = nextDisplay;
    setQuery(nextDisplay);
    setResults([]);
    setOpen(false);
    setLoading(false);
    setActiveIndex(-1);
    setError('');
    if (typeof onInputChange === 'function') onInputChange(nextDisplay);
    if (typeof onSelectPlace === 'function') onSelectPlace(place);
  }

  function handleKeyDown(event) {
    if (!showDropdown) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((prev) => {
        const next = prev + 1;
        return next >= results.length ? 0 : next;
      });
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((prev) => {
        const next = prev - 1;
        return next < 0 ? results.length - 1 : next;
      });
      return;
    }

    if (event.key === 'Enter') {
      if (activeIndex >= 0 && results[activeIndex]) {
        event.preventDefault();
        handleSelect(results[activeIndex]);
      }
      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      setOpen(false);
      setActiveIndex(-1);
    }
  }

  return (
    <div ref={rootRef} style={styles.wrap}>
      <label htmlFor={resolvedInputId} style={{ display: 'none' }}>
        {resolvedLabel}
      </label>
      <input
        id={resolvedInputId}
        name={resolvedInputName}
        type="text"
        value={query}
        disabled={disabled}
        style={{
          ...styles.input,
          ...(loading ? styles.inputLoading : null),
        }}
        onChange={handleChange}
        onFocus={() => {
          const trimmed = String(query || '').trim();
          if (
            trimmed.length >= minChars &&
            results.length > 0 &&
            trimmed !== lastCommittedSelectionRef.current
          ) {
            setOpen(true);
          }
        }}
        onMouseDown={() => {
          const trimmed = String(query || '').trim();
          if (
            trimmed.length >= minChars &&
            results.length > 0 &&
            trimmed !== lastCommittedSelectionRef.current
          ) {
            setOpen(true);
          }
        }}
        onKeyDown={handleKeyDown}
        placeholder={resolvedPlaceholder}
        autoComplete="off"
        spellCheck={false}
      />

      {showDropdown ? (
        <div style={styles.dropdown}>
          {loading ? <div style={styles.empty}>{localizedValue({ es: 'Buscando lugares…', en: 'Searching for places…' })}</div> : null}

          {!loading && error ? <div style={styles.empty}>{error}</div> : null}

          {!loading && !error && results.length === 0 ? (
            <div style={styles.empty}>{localizedValue({ es: 'No encontramos lugares para esa búsqueda.', en: 'We could not find any places for that search.' })}</div>
          ) : null}

          {!loading && !error
            ? results.map((place, index) => {
                const isActive = index === activeIndex;
                return (
                  <div
                    key={String(
                      place.id ||
                      place.placeId ||
                      place.featureId ||
                      `${place.lat ?? 'na'}-${place.lng ?? 'na'}-${place.label ?? 'place'}-${index}`
                    )}
                    style={{
                      ...styles.item,
                      ...(isActive ? styles.itemActive : null),
                    }}
                    onMouseDown={(event) => {
                      event.preventDefault();
                      handleSelect(place);
                    }}
                  >
                    <div style={styles.itemLabel}>{place.label}</div>
                    <div style={styles.itemMeta}>{formatMeta(place) || 'Lugar sin detalle regional'}</div>
                  </div>
                );
              })
            : null}
        </div>
      ) : null}

      {!showDropdown ? <div style={styles.hint}>{resolvedHint}</div> : null}
    </div>
  );
}
